import React, { useMemo } from "react";

import FilterOption from "@mapmycustomers/shared/enum/fieldModel/FilterOption";
import {
  EntityType,
  EntityTypesSupportedByMapsPage,
  EntityTypesSupportingRoutes,
  Group,
  MapFilterEntityType,
  Route,
} from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { FilterOptionValue } from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";
import { ModifiedMapFilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/MapFilterModel";
import SavedFilter from "@mapmycustomers/shared/types/viewModel/SavedFilter";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import { convertLatLngToLongLat } from "@mapmycustomers/shared/util/geo/GeoService";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import { useMap } from "@app/component/map/FeaturedMap";

import getMapFilterTypeForAnalytics from "../../utils/getMapFilterTypeForAnalytics";
import CreateFilterModal from "../filterModals/CreateFilterModal";

import EntityTypeFilters from "./components/EntityTypeFilters";
import Footer from "./components/Footer";
import HiddenEntitiesWarning from "./components/HiddenEntitiesWarning";
import NoFiltersState from "./components/NoFiltersState";
import SavedFilterState from "./components/SavedFilterState";
import styles from "./FilterDropdown.module.scss";

interface Props {
  checkInFieldsVisibility: boolean;
  context: string;
  entityTypes: MapFilterEntityType[];
  filteredFields: Partial<Record<MapFilterEntityType, IField[]>>;
  filterModel: ModifiedMapFilterModel;
  filterOptions?: Partial<Record<FilterOption, FilterOptionValue>>;
  focus?: { entityType: MapFilterEntityType; fieldName: string };
  groups: Record<EntityTypesSupportedByMapsPage, Group[]>;
  hiddenFields?: IField[];
  hideSavedFilters?: boolean;
  implicitFilterFields?: IField[];
  onChange?: (
    callback: (previousFilterModel: ModifiedMapFilterModel) => ModifiedMapFilterModel
  ) => void;
  onHideWarning?: () => void;
  routes: Record<EntityTypesSupportingRoutes, Route[]>;
  selectedFilter?: SavedFilter;
  visibleEntities: EntityTypesSupportedByMapsPage[];
  warningVisible?: boolean;
}

const FilterDropdown: React.FC<Props> = ({
  checkInFieldsVisibility,
  context,
  entityTypes,
  filteredFields,
  filterModel,
  filterOptions,
  focus,
  groups,
  hiddenFields,
  hideSavedFilters,
  implicitFilterFields,
  onChange,
  onHideWarning,
  routes,
  visibleEntities,
  warningVisible,
}) => {
  const mapContext = useMap();
  const hasAnyFilters = !!Object.values(filterModel).filter(isDefined).length;
  const [createFilterVisible, showCreateFilter, hideCreateFilter] = useBoolean();

  // split filterOptions per each entityType and merge appropriate AVAILABLE_GROUPS into buckets
  const filterOptionsByEntityType = useMemo(() => {
    const mapCenterLatLng = mapContext?.map?.getCenter();
    const mapCenter = mapCenterLatLng ? convertLatLngToLongLat(mapCenterLatLng) : undefined;
    return entityTypes.reduce(
      (result, entityType) => ({
        ...result,
        [entityType]: {
          ...filterOptions,
          [FilterOption.AVAILABLE_GROUPS]:
            entityType in groups ? groups[entityType as EntityTypesSupportedByMapsPage] : [],
          [FilterOption.AVAILABLE_ROUTES]:
            entityType in routes ? routes[entityType as EntityTypesSupportingRoutes] : [],
          [FilterOption.ENTITY_TYPE]: entityType,
          [FilterOption.MAP_CENTER]: mapCenter,
        },
      }),
      {} as Record<MapFilterEntityType, Partial<Record<FilterOption, FilterOptionValue>>>
    );
  }, [entityTypes, filterOptions, groups, mapContext?.map, routes]);

  return (
    <div className={styles.container}>
      {hasAnyFilters ? (
        <div className={styles.content}>
          <SavedFilterState
            entityType={EntityType.PIN}
            hideSavedFilters={hideSavedFilters}
            implicitFilterFields={implicitFilterFields}
            onCreate={showCreateFilter}
          />
          {warningVisible && (
            <HiddenEntitiesWarning onHide={onHideWarning} visibleEntities={visibleEntities} />
          )}
          {entityTypes
            .filter((entityType) => isDefined(filterModel[entityType]))
            .map((entityType) => (
              <ExtendedAnalytics
                key={entityType}
                keyPath={`${getMapFilterTypeForAnalytics(entityType)} Filters`}
              >
                <EntityTypeFilters
                  checkInFieldsVisibility={checkInFieldsVisibility}
                  context={context}
                  entityType={entityType}
                  filteredFields={filteredFields[entityType] ?? []}
                  filterModel={filterModel[entityType]!}
                  filterOptions={filterOptionsByEntityType[entityType]}
                  focusedField={focus?.entityType === entityType ? focus!.fieldName : undefined}
                  hiddenFields={hiddenFields}
                  onChange={onChange}
                  visibleEntities={visibleEntities}
                />
              </ExtendedAnalytics>
            ))}
          <Footer
            entityTypes={entityTypes}
            filterModel={filterModel}
            hideSavedFilters={hideSavedFilters}
            onChange={onChange}
            onCreateSavedFilter={showCreateFilter}
          />
          <CreateFilterModal
            entityType={EntityType.PIN}
            implicitFilterFields={implicitFilterFields}
            onHide={hideCreateFilter}
            visible={createFilterVisible}
          />
        </div>
      ) : (
        <>
          <SavedFilterState
            entityType={EntityType.PIN}
            implicitFilterFields={implicitFilterFields}
            onCreate={showCreateFilter}
          />
          <NoFiltersState entityTypes={entityTypes} onChange={onChange} />
        </>
      )}
    </div>
  );
};

export default FilterDropdown;
